import React, { useEffect, useState } from "react";

import AvatarCustom from "../../../components/AvatarCustom/AvatarCustom";
import { Box } from "@mui/material";
import { CloseIcon } from "../../../components/Icons";
import GvPrimaryButton from "../../../components/GvPrimaryButton/GvPrimaryButton";
import { IPetItemResponse } from "../../../services/pet-list/pet-list.interface";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { PAGES_PATH } from "../../../navigation/params";
import { isPrequalEligiblePet } from "../../../hooks/usePrequalFunnel";
import { useModal } from "../../../context/ModalContext";
import { useNavigate } from "react-router-dom";
import { usePetStore } from "../../../hooks/redux-hooks/pet-store/pet-store.service";
import { useUserStore } from "../../../hooks/redux-hooks/user-store/user.store";
import { logPrequalEvent } from "../PrequalificationContainer/prequalAnalytics";

// Pet picker step of the prequalification funnel,
// modeled on WellnessPlanSelectPetContainer without the wellness-member and
// pet-birthdate branching — the flow container owns all further gating.
const PrequalSelectPetContainer = () => {
  const { petList } = usePetStore();
  const { user } = useUserStore();
  const { closeModal } = useModal();
  const navigate = useNavigate();
  const [selectedPet, setSelectedPet] = useState<IPetItemResponse>();

  const eligiblePets = (petList ?? []).filter(isPrequalEligiblePet);

  useEffect(() => {
    logPrequalEvent(MIXPANEL_EVENTS.PREQUAL_PET_SELECT_SHOWN, {
      num_eligible_pets: eligiblePets.length,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinue = () => {
    if (!selectedPet) return;
    logPrequalEvent(MIXPANEL_EVENTS.PREQUAL_PET_SELECTED, {
      pet_id: selectedPet.id,
      pet_name: selectedPet.name,
    });
    closeModal();
    navigate(`${PAGES_PATH.PREQUALIFICATION}?id=${selectedPet.id}`);
  };

  return (
    <Box className="h-full flex flex-col justify-between p-2">
      <Box
        className="flex justify-end cursor-pointer w-full pb-2"
        onClick={closeModal}
      >
        <span className="bg-gray-6 p-0.5 rounded-full">
          <CloseIcon />
        </span>
      </Box>
      <Box className="w-full mb-2">
        <h1 className="text-3xl leading-8 grenette-pro mx-18 text-center my-2 font-semibold">
          Pre-qualification
        </h1>
        <p className="text-center">Select a Pet</p>
      </Box>
      <Box className="grow shrink mb-1 overflow-y-scroll px-1 flex flex-col gap-4">
        {eligiblePets.length > 0 ?
          eligiblePets.map((pet: IPetItemResponse) => (
            <div
              className={`border-2 rounded-lg p-4 cursor-pointer hover:border-gv-yellow
              ${selectedPet?.id === pet.id ? "border-gv-yellow bg-gv-yellow-light" : "border-taupe-50 bg-white"}`}
              onClick={() => setSelectedPet(pet)}
              key={pet.id}
            >
              <div className="flex gap-3 w-full items-center">
                <div>
                  <AvatarCustom
                    width={70}
                    height={70}
                    image={pet.image}
                    name={pet.name}
                  />
                </div>
                <div className="space-y-1">
                  <div className="font-semibold capitalize">
                    <p>{pet.name}</p>
                  </div>
                  {user?.location?.locationName && (
                    <div className="text-sm flex gap-1">
                      <span>Eligible at</span>
                      <span className="font-semibold">GoodVets {user.location.locationName}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )) :
          <div className="flex flex-col items-center justify-center text-gray-1 h-72">
            <div>Pre-qualification is only available for dogs and cats</div>
            <div>without an active TotalCare plan.</div>
          </div>
        }
      </Box>
      <Box className="!sticky !bottom-0 pt-1">
        <GvPrimaryButton
          type="button"
          className="!font-bold"
          isDisabled={!selectedPet}
          onClick={handleContinue}
        >
          Continue
        </GvPrimaryButton>
      </Box>
    </Box>
  );
};

export default PrequalSelectPetContainer;
